import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Building2, Save, Loader2, Upload, X, ImagePlus } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import type { Json } from '@/integrations/supabase/types';

interface CompanySettings {
  name: string;
  street: string;
  house_number: string;
  postal_code: string;
  city: string;
  country: string;
  kvk: string;
  btw: string;
  iban: string;
  bic: string;
  phone: string;
  email: string;
  website: string;
  logo_url: string;
}

const defaultSettings: CompanySettings = {
  name: '',
  street: '',
  house_number: '',
  postal_code: '',
  city: '',
  country: 'Nederland',
  kvk: '',
  btw: '',
  iban: '',
  bic: '',
  phone: '',
  email: '',
  website: '',
  logo_url: '',
};

export function CompanySettingsForm() {
  const [settings, setSettings] = useState<CompanySettings>(defaultSettings);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml'].includes(file.type)) {
      toast.error('Alleen PNG, JPG, WebP of SVG toegestaan');
      return;
    }
    
    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Maximum bestandsgrootte is 2MB');
      return;
    }
    
    setUploadingLogo(true);
    try {
      const ext = file.name.split('.').pop() || 'png';
      const fileName = `company-logo-${Date.now()}.${ext}`;
      
      // Delete old logo if exists
      if (settings.logo_url) {
        const oldPath = settings.logo_url.split('/').pop();
        if (oldPath) {
          await supabase.storage.from('company-assets').remove([oldPath]);
        }
      }
      
      const { error: uploadError } = await supabase.storage
        .from('company-assets')
        .upload(fileName, file, { upsert: true });
      
      if (uploadError) throw uploadError;
      
      const { data: { publicUrl } } = supabase.storage
        .from('company-assets')
        .getPublicUrl(fileName);
      
      setSettings(prev => ({ ...prev, logo_url: publicUrl }));
      toast.success('Logo geüpload');
    } catch (error: any) {
      console.error('Logo upload error:', error);
      toast.error('Upload mislukt: ' + error.message);
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleRemoveLogo = async () => {
    if (settings.logo_url) {
      const oldPath = settings.logo_url.split('/').pop();
      if (oldPath) {
        await supabase.storage.from('company-assets').remove([oldPath]);
      }
    }
    setSettings(prev => ({ ...prev, logo_url: '' }));
    toast.success('Logo verwijderd');
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('app_settings')
        .select('value')
        .eq('key', 'company_settings')
        .maybeSingle();

      if (error) throw error;

      if (data?.value && typeof data.value === 'object' && !Array.isArray(data.value)) {
        setSettings({ ...defaultSettings, ...(data.value as unknown as CompanySettings) });
      }
    } catch (error) {
      console.error('Error fetching company settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      // First check if record exists
      const { data: existing } = await supabase
        .from('app_settings')
        .select('key')
        .eq('key', 'company_settings')
        .maybeSingle();

      if (existing) {
        // Update existing
        const { error } = await supabase
          .from('app_settings')
          .update({
            value: settings as unknown as Json,
            updated_at: new Date().toISOString(),
          })
          .eq('key', 'company_settings');

        if (error) throw error;
      } else {
        // Insert new
        const { error } = await supabase
          .from('app_settings')
          .insert({
            key: 'company_settings',
            value: settings as unknown as Json,
          });

        if (error) throw error;
      }

      toast.success('Bedrijfsgegevens opgeslagen');
    } catch (error) {
      console.error('Error saving company settings:', error);
      toast.error('Fout bij opslaan bedrijfsgegevens');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field: keyof CompanySettings, value: string) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Building2 className="h-5 w-5" />
          Bedrijfsgegevens
        </CardTitle>
        <CardDescription>
          Deze gegevens worden gebruikt in offerte templates via placeholders zoals {'{{company_name}}'}, {'{{company_kvk}}'}, etc.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSave} className="space-y-6">
          {/* Logo Upload */}
          <div className="space-y-3">
            <Label>Bedrijfslogo</Label>
            <div className="flex items-start gap-4">
              {/* Preview */}
              {settings.logo_url ? (
                <div className="relative w-40 h-24 border rounded-lg overflow-hidden bg-muted flex items-center justify-center">
                  <img 
                    src={settings.logo_url} 
                    alt="Bedrijfslogo" 
                    className="max-w-full max-h-full object-contain p-2"
                  />
                  <Button
                    type="button"
                    variant="destructive"
                    size="icon"
                    className="absolute top-1 right-1 h-6 w-6"
                    onClick={handleRemoveLogo}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <div className="w-40 h-24 border-2 border-dashed rounded-lg flex items-center justify-center bg-muted/50">
                  <ImagePlus className="h-8 w-8 text-muted-foreground" />
                </div>
              )}
              
              {/* Upload Button */}
              <div className="flex flex-col gap-2">
                <input
                  type="file"
                  id="logo-upload"
                  className="hidden"
                  accept="image/png,image/jpeg,image/webp,image/svg+xml"
                  onChange={handleLogoUpload}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => document.getElementById('logo-upload')?.click()}
                  disabled={uploadingLogo}
                >
                  {uploadingLogo ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Upload className="mr-2 h-4 w-4" />
                  )}
                  Logo uploaden
                </Button>
                <p className="text-xs text-muted-foreground">
                  PNG, JPG, WebP of SVG. Max 2MB.<br />
                  Gebruik placeholder: {'{{company_logo}}'}
                </p>
              </div>
            </div>
          </div>

          {/* Bedrijfsnaam */}
          <div className="space-y-2">
            <Label htmlFor="company_name">Bedrijfsnaam *</Label>
            <Input
              id="company_name"
              value={settings.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="GreenVision B.V."
            />
          </div>

          {/* Adres */}
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="sm:col-span-2 space-y-2">
              <Label htmlFor="street">Straatnaam</Label>
              <Input
                id="street"
                value={settings.street}
                onChange={(e) => handleChange('street', e.target.value)}
                placeholder="Industrieweg"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="house_number">Huisnummer</Label>
              <Input
                id="house_number"
                value={settings.house_number}
                onChange={(e) => handleChange('house_number', e.target.value)}
                placeholder="10"
              />
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-2">
              <Label htmlFor="postal_code">Postcode</Label>
              <Input
                id="postal_code"
                value={settings.postal_code}
                onChange={(e) => handleChange('postal_code', e.target.value)}
                placeholder="1234 AB"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="city">Plaats</Label>
              <Input
                id="city"
                value={settings.city}
                onChange={(e) => handleChange('city', e.target.value)}
                placeholder="Amsterdam"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="country">Land</Label>
              <Input
                id="country"
                value={settings.country}
                onChange={(e) => handleChange('country', e.target.value)}
                placeholder="Nederland"
              />
            </div>
          </div>

          {/* Registratienummers */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="kvk">KvK nummer *</Label>
              <Input
                id="kvk"
                value={settings.kvk}
                onChange={(e) => handleChange('kvk', e.target.value)}
                placeholder="12345678"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="btw">BTW nummer *</Label>
              <Input
                id="btw"
                value={settings.btw}
                onChange={(e) => handleChange('btw', e.target.value)}
                placeholder="NL123456789B01"
              />
            </div>
          </div>

          {/* Bankgegevens */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="iban">IBAN *</Label>
              <Input
                id="iban"
                value={settings.iban}
                onChange={(e) => handleChange('iban', e.target.value)}
                placeholder="NL00 BANK 1234 5678 90"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="bic">BIC/SWIFT code</Label>
              <Input
                id="bic"
                value={settings.bic}
                onChange={(e) => handleChange('bic', e.target.value)}
                placeholder="ABNANL2A"
              />
            </div>
          </div>

          {/* Contactgegevens */}
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-2">
              <Label htmlFor="phone">Telefoonnummer</Label>
              <Input
                id="phone"
                value={settings.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                placeholder="+31 20 123 4567"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">E-mailadres</Label>
              <Input
                id="email"
                type="email"
                value={settings.email}
                onChange={(e) => handleChange('email', e.target.value)}
                placeholder="info@bedrijf.nl"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="website">Website</Label>
              <Input
                id="website"
                value={settings.website}
                onChange={(e) => handleChange('website', e.target.value)}
                placeholder="www.bedrijf.nl"
              />
            </div>
          </div>

          <div className="flex justify-end pt-4">
            <Button type="submit" disabled={saving}>
              {saving ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Save className="mr-2 h-4 w-4" />
              )}
              {saving ? 'Opslaan...' : 'Bedrijfsgegevens opslaan'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
